import {Helmet} from '@dr.pogodin/react-helmet';
import {skipToken} from '@reduxjs/toolkit/query';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus, Parameter} from 'platform/components';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import type {ComponentProps} from 'react';

import {defaultTo, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import type {TenantResponseBody} from '@dms/api/shared';
import {
  useGetArticleQuery,
  useGetHandlingUnitsQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  useGetWarehouseQuery,
  useGetWarehousesQuery,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  createHeaderActions,
  DetailTemplate,
  DetailTemplateHeader,
  getUserName,
  handleApiError,
  NavigationItem,
  Section,
  useChecklist,
} from '@dms/shared';
import {isPermissionError} from '@dms/shared-aftersales';
import {getHandlingUnitLabel} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {
  ApiError,
  buildArray,
  composePath,
  EMPTY_PLACEHOLDER,
  Nullish,
  parseDate,
  useNavigate,
} from 'shared/web';

import {useArticleAllowedPermissions} from '../../hooks/useArticleAllowedPermissions';
import {useArticleSellingPriceWarning} from '../../hooks/useArticleSellingPriceWarning';
import {Availability} from './(sections)/Availability/Availability';
import {Documents} from './(sections)/Documents/Documents';
import {History} from './(sections)/History/History';
import {Locations} from './(sections)/Locations/Locations';
import {Movements} from './(sections)/Movements/Movements';
import {Overview} from './(sections)/Overview/Overview';
import {Receipts} from './(sections)/Receipts/Receipts';
import {Reservations} from './(sections)/Reservations/Reservations';
import {Statistics} from './(sections)/Statistics/Statistics';
import {SupplierArticles} from './(sections)/SupplierArticles/SupplierArticles';
import {UpdateJobsLog} from './(sections)/UpdateJobsLog/UpdateJobsLog';

const getWarehouseAclRefusalMessage = (errors: (ApiError | Nullish)[]) =>
  isFeatureEnabled(featureFlags.AFS_WAREHOUSE_ACL_SCOPES) &&
  errors.some((error) => isNotNil(error) && isPermissionError(error))
    ? i18n.t('general.notifications.noPermission')
    : undefined;

const getArticleAlerts = (
  canEdit: boolean,
  sellingPriceWarning: ReturnType<typeof useArticleSellingPriceWarning>['sellingPriceWarning']
): DetailTemplateHeader['alerts'] =>
  buildArray<NonNullable<DetailTemplateHeader['alerts']>[number]>()
    .whenNot(canEdit, {
      type: 'banner',
      variant: 'info',
      message: i18n.t('module.warehouse.label.missingArticleEditPermission'),
    })
    .whenNotNil(sellingPriceWarning, (warning) => ({
      type: 'banner',
      variant: 'warning',
      message: i18n.t(warning),
    }));

interface StatisticsNavigationParams {
  params: ReturnType<typeof useWarehouseParams>;
  tenant: TenantResponseBody;
  handlingUnitLabel: string;
}

function getStatisticsNavigation({
  params,
  tenant,
  handlingUnitLabel,
}: StatisticsNavigationParams): NavigationItem {
  return {
    id: 'statistics',
    label: i18n.t('page.filters.labels.statistics'),
    href: composePath(warehouseRoutes.articleDetailStatistics, {
      params: {id: params.articleId, warehouseId: params.warehouseId},
    }),
    content: <Statistics tenant={tenant} handlingUnitLabel={handlingUnitLabel} />,
  };
}

function getOverviewNavigation(
  params: ReturnType<typeof useWarehouseParams>,
  overviewProps: ComponentProps<typeof Overview>
): NavigationItem {
  return {
    id: 'article',
    label: i18n.t('page.warehouse.labels.overview'),
    href: composePath(warehouseRoutes.articleDetailOverview, {
      params: {id: params.articleId, warehouseId: params.warehouseId},
    }),
    content: <Overview {...overviewProps} />,
    hasSeparator: true,
    'data-testid': testIds.warehouse.articleDetailOverview('article'),
  };
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function ArticleDetail() {
  const params = useWarehouseParams();
  const currencyFormatter = useFormatCurrency();
  const navigate = useNavigate();
  const dateFormatter = useDateTimeFormatter();

  const articleQuery = useGetArticleQuery({
    articleId: params.articleId,
    warehouseId: params.warehouseId,
    isAclScoped: true,
  });
  const {articleRevision, sellingPriceWarning, handleSellingPriceWarningChange} =
    useArticleSellingPriceWarning(articleQuery.data);
  const {canEdit} = useArticleAllowedPermissions(articleQuery.data);
  const {
    data: article,
    isLoading: isArticleLoading,
    isFetching: isArticleFetching,
    isError: isArticleError,
    error: articleError,
  } = articleQuery;

  const headerActionsQuery = useGetWarehouseHeaderDynamicActionsQuery(
    {id: params.articleId, resource: 'article', isAclScoped: true},
    {skip: isNilOrEmpty(params.articleId)}
  );

  const [putHeaderAction, headerActionStatus] = usePutWarehouseHeaderDynamicActionsMutation();

  const {
    data: warehouse,
    isLoading: isWarehouseLoading,
    isError: isWarehouseError,
    error: warehouseError,
  } = useGetWarehouseQuery(
    isNotNil(article) ? {warehouseId: article.warehouseId, isAclScoped: true} : skipToken
  );

  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery({isAclScoped: true});

  const {data: tenant, isLoading: isTenantLoading, isError: isTenantError} = useGetTenantQuery();

  const {
    data: handlingUnits,
    isLoading: isHandlingUnitsLoading,
    isError: isHandlingUnitsError,
  } = useGetHandlingUnitsQuery();

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-article', contextId: params.articleId},
    {skip: !params.articleId}
  );

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-article',
    contextId: params.articleId,
  });

  const {
    data: createdByUser,
    isLoading: isCreatedByUserLoading,
    isError: isCreatedByUserError,
  } = useGetUserQuery({id: article?.createdBy as string}, {skip: isNilOrEmpty(article?.createdBy)});

  const {
    data: updatedByUser,
    isLoading: isUpdatedByUserLoading,
    isError: isUpdatedByUserError,
  } = useGetUserQuery({id: article?.updatedBy as string}, {skip: isNilOrEmpty(article?.updatedBy)});

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: params.articleId,
    resourceId: 'WAREHOUSE_ARTICLE',
  });

  // A deep link to an article in a warehouse the user cannot read lands on the permission message
  const aclRefusalMessage = getWarehouseAclRefusalMessage([
    articleError,
    warehouseError,
    headerActionsQuery.error,
  ]);

  if (
    isNotNil(aclRefusalMessage) ||
    !article ||
    !warehouses ||
    !tenant ||
    !warehouse ||
    !handlingUnits ||
    !createdByUser ||
    !updatedByUser
  ) {
    return (
      <DataStatus
        isLoading={
          isArticleLoading ||
          isWarehousesLoading ||
          isTenantLoading ||
          isWarehouseLoading ||
          isCreatedByUserLoading ||
          isUpdatedByUserLoading ||
          isHandlingUnitsLoading
        }
        isError={
          isNotNil(aclRefusalMessage) ||
          isArticleError ||
          isWarehousesError ||
          isTenantError ||
          isWarehouseError ||
          isCreatedByUserError ||
          isUpdatedByUserError ||
          isHandlingUnitsError
        }
        errorMessage={aclRefusalMessage}
        minHeight="100vh"
      />
    );
  }

  const priceWithoutVat = defaultTo(0, article?.dispensingPrices?.saleBasePriceWithoutVat);
  const priceWithVat = defaultTo(0, article?.dispensingPrices?.saleBasePriceWithVat);
  const currencyWithVat = currencyFormatter(priceWithVat, tenant.currency, 2);
  const currencyWithoutVat = currencyFormatter(priceWithoutVat, tenant.currency, 2);
  const createdDate = dateFormatter('dateTimeMedium', parseDate(article.created));
  const updatedDate = isNotNil(article.updated)
    ? dateFormatter('dateTimeMedium', parseDate(article.updated))
    : EMPTY_PLACEHOLDER;

  const handlingUnitLabel = getHandlingUnitLabel(article.handlingUnit, handlingUnits);
  const title = `${i18n.t('entity.warehouse.labels.article')} ${article.name}`;
  const maximization = {enabled: true, title};

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'reserve') {
      handleReserve();
      return;
    }

    putHeaderAction({
      id: params.articleId,
      resource: 'article',
      body: {actionKey},
    })
      .unwrap()
      .then(() => {
        articleQuery.refetch();
      })
      .catch(handleApiError);
  };

  const handleReserve = () =>
    navigate(
      composePath(warehouseRoutes.articleDetailReservations, {
        params: {warehouseId: params.warehouseId, id: params.articleId},
      })
    );

  const createdBy = `${i18n.t('general.labels.createdBy')}: ${getUserName(createdByUser) ?? EMPTY_PLACEHOLDER}`;
  const updatedBy = `${i18n.t('general.labels.updatedBy')}: ${getUserName(updatedByUser) ?? EMPTY_PLACEHOLDER}`;

  const dynamicActions = createHeaderActions({
    actions: headerActionsQuery.data?.actions,
    callback: handleActionClick,
    isLoading: headerActionStatus.isLoading,
  });

  const getHeader: DetailTemplateHeader = {
    title: article.name,
    isEntityLoading: isArticleFetching || headerActionsQuery.isFetching,
    icon: 'custom/warehouse',
    parameters: buildArray<Parameter>()
      .add(article.manufacturerNumber)
      .whenNotNil(article.manufacturerNumberNormalized)
      .add(`${i18n.t('general.labels.created')}: ${createdDate}`)
      .add(createdBy)
      .add(updatedBy)
      .when(article.updated, `${i18n.t('general.labels.lastUpdate')}: ${updatedDate}`)
      .add(warehouse?.name)
      .when(article.storageLocation, article.storageLocation),
    recordId: article.id,
    actions: dynamicActions,
    controls: ['ASSIGNEE'],
    primaryParameter: defaultTo(undefined, currencyWithVat),
    secondaryParameter: `${currencyWithoutVat} ${i18n.t('general.labels.w/oVat')}`,
    alerts: getArticleAlerts(canEdit, sellingPriceWarning),
  };

  const navigation = buildArray<NavigationItem>()
    .add(
      getOverviewNavigation(params, {
        maximization,
        article,
        warehouse,
        warehouses,
        tenant,
        handlingUnitLabel,
        onSellingPriceWarningChange: handleSellingPriceWarningChange,
        'data-testid': testIds.warehouse.articleDetailOverview('sections.article'),
      })
    )
    .add({
      id: 'availability',
      label: i18n.t('entity.warehouse.labels.availability'),
      href: composePath(warehouseRoutes.articleDetailAvailability, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Availability
          maximization={maximization}
          handlingUnitLabel={handlingUnitLabel}
          data-testid={testIds.warehouse.articleDetailAvailability('sections.availability')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailAvailability('availability'),
    })
    .add({
      id: 'locations',
      label: i18n.t('entity.warehouse.labels.locations'),
      href: composePath(warehouseRoutes.articleDetailLocations, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Locations
          maximization={maximization}
          data-testid={testIds.warehouse.articleDetailLocations('sections.locations')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailLocations('locations'),
    })
    .add({
      id: 'receipts',
      label: i18n.t('entity.warehouse.labels.receipts'),
      href: composePath(warehouseRoutes.articleDetailReceipts, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Receipts
          maximization={maximization}
          tenant={tenant}
          handlingUnitLabel={handlingUnitLabel}
          data-testid={testIds.warehouse.articleDetailReceipts('sections.receipts')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailReceipts('receipts'),
    })
    .add({
      id: 'movements',
      label: i18n.t('entity.warehouse.labels.movements'),
      href: composePath(warehouseRoutes.articleDetailMovements, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Movements
          maximization={maximization}
          data-testid={testIds.warehouse.articleDetailMovements('sections.movements')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailMovements('movements'),
    })
    .add(getStatisticsNavigation({params, tenant, handlingUnitLabel}))
    .add({
      id: 'history',
      label: i18n.t('general.labels.history'),
      href: composePath(warehouseRoutes.articleDetailHistory, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: <History maximization={maximization} />,
      'data-testid': testIds.warehouse.articleDetailHistory('history'),
    })
    .add({
      id: 'reservations',
      label: i18n.t('entity.warehouse.labels.reservations'),
      href: composePath(warehouseRoutes.articleDetailReservations, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Reservations
          maximization={maximization}
          handlingUnitLabel={handlingUnitLabel}
          data-testid={testIds.warehouse.articleDetailReservations('sections.reservations')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailReservations('reservations'),
    })
    .add({
      id: 'supplierArticles',
      label: i18n.t('entity.warehouse.labels.supplierArticles'),
      href: composePath(warehouseRoutes.articleDetailSupplierArticles, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <SupplierArticles
          maximization={maximization}
          authorizationProfileId={warehouse?.authorizationProfileId}
          data-testid={testIds.warehouse.articleDetailSupplierArticles('sections.supplierArticles')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailSupplierArticles('supplierArticles'),
    })
    .add({
      id: 'updateJobsLog',
      label: i18n.t('entity.warehouse.labels.updateJobsLog'),
      href: composePath(warehouseRoutes.articleDetailUpdateJobsLog, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <UpdateJobsLog
          maximization={maximization}
          data-testid={testIds.warehouse.articleDetailUpdateJobsLog('sections.updateJobsLog')}
        />
      ),
      'data-testid': testIds.warehouse.articleDetailUpdateJobsLog('updateJobsLog'),
    })
    .add({
      id: 'documents',
      label: i18n.t('entity.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.articleDetailDocuments, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Documents
          maximization={maximization}
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.articleDetailDocuments('sections.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.articleDetailDocuments('documents'),
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: 'checklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(warehouseRoutes.articleDetailChecklists, {
        params: {id: params.articleId, warehouseId: params.warehouseId},
      }),
      content: (
        <Section>
          <Checklists
            {...checklistSettings}
            contextTarget="warehouse-article"
            contextId={params.articleId}
          />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.warehouse.articleDetailChecklists('checklists'),
    });

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.articleDetail')} />
      <DetailTemplate
        key={articleRevision}
        isLoading={isArticleLoading || headerActionsQuery.isLoading}
        isError={isArticleError || headerActionsQuery.isError}
        isLocked={headerActionStatus.isLoading}
        header={getHeader}
        navigation={navigation}
        data-testid={testIds.warehouse.articleDetailOverview('page')}
      />
    </>
  );
}
